/**
 * Add Customer Button
 *
 * Small "+" button that opens a ComboBox for linking an **Account** (from the
 * Accounts board) to a task. The dropdown lists the org's account cards;
 * selecting one links it (the customer name mirrors the account title and the
 * badge links back to that account card).
 *
 * @module features/tasks/components/AddCustomerButton
 */

import React from 'react';
import { Button, ComboBox, Flex, useToast, type ComboBoxOption } from '@chatsdet/ui';
import { Plus } from '@phosphor-icons/react';
import { useAddCustomer } from '../hooks/useTasks';
import { useCards } from '../../cards/hooks/useCards';

interface AddCustomerButtonProps {
  taskId: string;
}

export function AddCustomerButton({ taskId }: AddCustomerButtonProps) {
  const [isAdding, setIsAdding] = React.useState(false);
  const [inputValue, setInputValue] = React.useState('');
  const addCustomer = useAddCustomer();
  const { toast } = useToast();
  const { data: accounts = [], isLoading } = useCards('account');

  const options: ComboBoxOption[] = React.useMemo(
    () =>
      accounts.map((account) => ({
        id: account.id,
        label: account.title,
        textValue: account.title,
      })),
    [accounts]
  );

  const handleSelect = (key: string | null) => {
    if (!key) return;
    const account = accounts.find((a) => a.id === key);
    if (!account) return;
    addCustomer.mutate(
      { taskId, accountCardId: account.id },
      {
        onSuccess: () => {
          toast.success(`Linked ${account.title}`);
          setIsAdding(false);
          setInputValue('');
        },
        onError: () => toast.error('Failed to link account'),
      }
    );
  };

  if (!isAdding) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="w-6 p-0"
        onPress={() => setIsAdding(true)}
        aria-label="Link account"
      >
        <Plus size={12} />
      </Button>
    );
  }

  return (
    <Flex align="center" gap="xs">
      <ComboBox
        items={options}
        placeholder={isLoading ? 'Loading accounts…' : 'Search accounts…'}
        inputValue={inputValue}
        onInputChange={setInputValue}
        onSelectionChange={(key) => handleSelect(key as string | null)}
        isDisabled={addCustomer.isPending}
        className="w-[240px]"
        menuTrigger="focus"
      />
      <Button
        variant="ghost"
        size="sm"
        onPress={() => {
          setIsAdding(false);
          setInputValue('');
        }}
      >
        Cancel
      </Button>
    </Flex>
  );
}
